/**
 * A fixed number of real lights, handed to whichever sources are nearest
 * (2026-10-15, QA #9: "GPU at 94%, heavy FPS drops and hitches").
 *
 * Every brazier in the dungeon was its own `PointLight` in the scene — around
 * thirty on a floor, one per sconce in every room — and three.js forward
 * shading evaluates *every* light in the scene for every lit fragment, however
 * far away it is: the range cut-off happens inside the loop, not before it. So
 * each pixel of a wall paid for thirty torches it could not see.
 *
 * Worse, the light *count* is baked into every material's shader. A lantern
 * being lit for the first time, or a brazier arriving in state, changed it,
 * and every material in the scene recompiled on that frame — a hitch.
 *
 * So the scene keeps a constant pool and this decides who gets them: the
 * nearest sources to the camera, with each source keeping the slot it already
 * holds so a light never jumps between torches mid-frame. Pure, so the rule is
 * pinned in tests rather than argued about.
 */
export interface LightSourcePos {
  id: string;
  x: number;
  z: number;
}

/**
 * The source id for each slot (null = slot unused), given what the slots held
 * last time. A source still among the nearest `slots.length` keeps its slot;
 * newcomers fill the free ones nearest-first.
 */
export function assignLightSlots(
  previous: readonly (string | null)[],
  sources: readonly LightSourcePos[],
  focus: { x: number; z: number },
): (string | null)[] {
  const slotCount = previous.length;
  const nearest = [...sources]
    .sort((a, b) => Math.hypot(a.x - focus.x, a.z - focus.z) - Math.hypot(b.x - focus.x, b.z - focus.z))
    .slice(0, slotCount)
    .map((s) => s.id);
  const chosen = new Set(nearest);
  const next = previous.map((id) => (id !== null && chosen.has(id) ? id : null));
  const placed = new Set(next.filter((id): id is string => id !== null));
  let free = 0;
  for (const id of nearest) {
    if (placed.has(id)) continue;
    while (free < slotCount && next[free] !== null) free++;
    if (free >= slotCount) break;
    next[free] = id;
  }
  return next;
}
